// components/dashboard/credits/CreditsBuyEmailsModal.tsx
'use client'

import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Slider } from '@/components/ui/slider'
import { cn } from '@/lib/utils'

import { CheckoutFormBlock } from '@/components/funnel/blocks/CheckoutFormBlock'
import { buildPurchaseContext } from '@/lib/checkout/buildPurchaseContext'

type EmailTier = {
  tierKey: string
  name: string
  emailsMonthly: number
  priceUsdMonthly: number
  priceLabel?: string // "$99.00 USD / mes"
  best?: boolean
  description?: string
  features?: string[]
}

type TiersRes = { ok: boolean; tiers: EmailTier[] }

// “product like” como en tu checkout
type ProductLike = any
type Step = 'pick' | 'checkout'

function moneyUsd(n: number) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(Number(n || 0))
  } catch {
    return `$${Number(n || 0).toFixed(2)}`
  }
}

function perThousand(priceUsdMonthly: number, emailsMonthly: number) {
  const p = Number(priceUsdMonthly || 0)
  const e = Number(emailsMonthly || 0)
  if (!p || !e) return null
  const v = (p / e) * 1000
  return v
}

function formatInt(n: number) {
  return Number(n || 0).toLocaleString('es-MX')
}

export function CreditsBuyEmailsModal({
  open,
  onOpenChange,
  onEmailsUpdated,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onEmailsUpdated?: () => void
}) {
  const [step, setStep] = useState<Step>('pick')

  // tiers
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [tiers, setTiers] = useState<EmailTier[]>([])

  // slider idx
  const [idx, setIdx] = useState(0)

  // checkout
  const [loadingProduct, setLoadingProduct] = useState(false)
  const [productErr, setProductErr] = useState<string | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<ProductLike | null>(null)
  const [selectedTierKey, setSelectedTierKey] = useState<string>('')

  // reset al cerrar
  useEffect(() => {
    if (!open) {
      setStep('pick')
      setErr(null)
      setLoading(false)
      setTiers([])
      setIdx(0)

      setLoadingProduct(false)
      setProductErr(null)
      setSelectedProduct(null)
      setSelectedTierKey('')
    }
  }, [open])

  // load tiers al abrir
  useEffect(() => {
    if (!open) return
    let alive = true

    ;(async () => {
      setLoading(true)
      setErr(null)
      try {
        const r = await fetch(`/api/emails/tiers`, { method: 'GET', cache: 'no-store' })
        const j = await r.json().catch(() => null)

        if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`)
        if (!j?.ok) throw new Error(j?.error || 'API_NOT_OK')

        const list = Array.isArray(j?.tiers) ? j.tiers : []
        if (!alive) return

        // orden defensivo por emailsMonthly
        list.sort((a: EmailTier, b: EmailTier) => Number(a.emailsMonthly || 0) - Number(b.emailsMonthly || 0))

        setTiers(list)
        // default: el “best” o el primero
        const bestIndex = Math.max(
          0,
          list.findIndex((x: EmailTier) => !!x.best)
        )
        setIdx(bestIndex >= 0 ? bestIndex : 0)
      } catch (e: any) {
        if (alive) setErr(e?.message || 'LOAD_FAILED')
      } finally {
        if (alive) setLoading(false)
      }
    })()

    return () => {
      alive = false
    }
  }, [open])

  const selectedTier = useMemo(() => {
    if (!tiers.length) return null
    return tiers[Math.min(Math.max(idx, 0), tiers.length - 1)] || null
  }, [tiers, idx])

  const modalClass = useMemo(() => {
    // encoge en checkout como credits
    return cn('rounded-3xl p-0 overflow-hidden', step === 'pick' ? 'max-w-2xl' : 'max-w-md')
  }, [step])

  async function loadTierProduct(tier: EmailTier) {
    const tierKey = String(tier.tierKey || '')
    setSelectedTierKey(tierKey)

    setLoadingProduct(true)
    setProductErr(null)
    setSelectedProduct(null)

    try {
      const r = await fetch(`/api/emails/product?tier=${encodeURIComponent(tierKey)}`, { cache: 'no-store' })
      const j = await r.json().catch(() => null)

      if (!r.ok) {
        setProductErr(j?.error || `HTTP ${r.status}`)
        return
      }

      // endpoint regresa producto “plano”
      setSelectedProduct(j)
    } catch (e) {
      console.error('emails/product error', e)
      setProductErr('Error al cargar el producto.')
    } finally {
      setLoadingProduct(false)
    }
  }

  async function goCheckout() {
    if (!selectedTier) return
    setStep('checkout')
    await loadTierProduct(selectedTier)
  }

  function backToPick() {
    setStep('pick')
    setLoadingProduct(false)
    setProductErr(null)
    setSelectedProduct(null)
    setSelectedTierKey('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={modalClass}>
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span>{step === 'pick' ? 'Upgrade de emails' : 'Checkout'}</span>

              {step === 'checkout' && (
                <Button variant="secondary" className="rounded-2xl" onClick={backToPick}>
                  Volver
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* STEP 1: PICK (slider) */}
          {step === 'pick' && (
            <div className="mt-4 space-y-4">
              {loading && (
                <div className="space-y-3">
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-10 w-full rounded-2xl" />
                  <Skeleton className="h-24 w-full rounded-3xl" />
                </div>
              )}

              {!loading && err && (
                <div className="text-sm text-muted-foreground">
                  Error cargando tiers: {err}{' '}
                  <Button
                    variant="secondary"
                    className="ml-2 rounded-2xl"
                    onClick={() => {
                      onOpenChange(false)
                      setTimeout(() => onOpenChange(true), 50)
                    }}
                  >
                    Reintentar
                  </Button>
                </div>
              )}

              {!loading && !err && tiers.length > 0 && selectedTier && (
                <>
                  <div className="rounded-3xl border border-border bg-card/70 backdrop-blur">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-lg font-semibold truncate">{selectedTier.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatInt(selectedTier.emailsMonthly)} emails / mes
                          </div>
                        </div>

                        {selectedTier.best && (
                          <Badge className="rounded-full bg-foreground text-background">Mejor valor</Badge>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-3">
                      <div className="flex items-end justify-between gap-3">
                        <div className="text-2xl font-semibold tabular-nums">
                          {selectedTier.priceLabel ||
                            `${moneyUsd(selectedTier.priceUsdMonthly)} / mes`}
                        </div>

                        {perThousand(selectedTier.priceUsdMonthly, selectedTier.emailsMonthly) !== null && (
                          <div className="text-xs text-muted-foreground text-right">
                            <div className="font-medium">
                              {moneyUsd(perThousand(selectedTier.priceUsdMonthly, selectedTier.emailsMonthly)!)} / 1,000
                            </div>
                            <div>entre más volumen, más barato</div>
                          </div>
                        )}
                      </div>

                      {selectedTier.description && (
                        <div className="text-sm text-muted-foreground">{selectedTier.description}</div>
                      )}

                      {Array.isArray(selectedTier.features) && selectedTier.features.length > 0 && (
                        <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                          {selectedTier.features.slice(0, 4).map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </div>

                  {/* Slider */}
                  <div className="rounded-3xl border border-border bg-background/40 p-4 space-y-3">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatInt(tiers[0].emailsMonthly)} / mes</span>
                      <span>{formatInt(tiers[tiers.length - 1].emailsMonthly)} / mes</span>
                    </div>

                    <Slider
                      value={[idx]}
                      min={0}
                      max={Math.max(0, tiers.length - 1)}
                      step={1}
                      onValueChange={(v) => {
                        const next = Array.isArray(v) ? Number(v[0] || 0) : 0
                        setIdx(next)
                      }}
                    />

                    {/* Snap labels (compact) */}
                    <div className="grid grid-cols-7 gap-1 text-[11px] text-muted-foreground">
                      {tiers.map((t, i) => {
                        const active = i === idx
                        // compact label
                        const n = Number(t.emailsMonthly || 0)
                        const label =
                          n >= 1_000_000
                            ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
                            : n >= 1000
                              ? `${Math.round(n / 1000)}k`
                              : String(n)

                        return (
                          <div
                            key={t.tierKey}
                            className={cn(
                              'truncate text-center',
                              active ? 'text-foreground font-medium' : 'opacity-70'
                            )}
                            title={t.name}
                          >
                            {label}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <Button variant="secondary" className="rounded-2xl" onClick={() => onOpenChange(false)}>
                      Cancelar
                    </Button>
                    <Button className="rounded-2xl" onClick={goCheckout}>
                      Continuar
                    </Button>
                  </div>
                </>
              )}

              {!loading && !err && tiers.length === 0 && (
                <div className="text-sm text-muted-foreground">No hay tiers disponibles.</div>
              )}
            </div>
          )}

          {/* STEP 2: CHECKOUT */}
          {step === 'checkout' && selectedTier && (
            <div className="mt-4 space-y-3">
              {/* resumen */}
              <div className="rounded-2xl border border-border bg-background/40 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{selectedTier.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatInt(selectedTier.emailsMonthly)} / mes ·{' '}
                      {selectedTier.priceLabel || `${moneyUsd(selectedTier.priceUsdMonthly)} / mes`}
                    </div>
                  </div>
                </div>
              </div>

              {loadingProduct && (
                <div className="rounded-2xl border border-border bg-card/60 backdrop-blur p-4 space-y-3">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-10 w-full rounded-2xl" />
                  <Skeleton className="h-10 w-full rounded-2xl" />
                  <Skeleton className="h-10 w-full rounded-2xl" />
                </div>
              )}

              {!loadingProduct && productErr && (
                <div className="rounded-2xl border border-border bg-background/40 p-4 text-sm text-muted-foreground">
                  Error preparando checkout: {productErr}
                  <div className="mt-3 flex gap-2">
                    <Button variant="secondary" className="rounded-2xl" onClick={() => loadTierProduct(selectedTier)}>
                      Reintentar
                    </Button>
                    <Button variant="ghost" className="rounded-2xl" onClick={backToPick}>
                      Cambiar tier
                    </Button>
                  </div>
                </div>
              )}

              {!loadingProduct && !productErr && selectedProduct && (
                <CheckoutFormBlock
                  data={{
                    ...buildPurchaseContext({
                      product: selectedProduct,
                      activeRecurring:
                        selectedProduct?.recurrings?.find(
                          (r: any) => r.recurring_id === selectedProduct?.selectedRecurringId
                        ) ||
                        selectedProduct?.recurrings?.[0] ||
                        null,
                      selectedRecurringId: selectedProduct?.selectedRecurringId || null,
                      price: selectedProduct?.price,
                      priceUSD: selectedProduct?.priceUSD,
                      paymentMethods: selectedProduct?.paymentMethods,

                      // ✅ platform marker (como storage/membership/credits)
                      platform: {
                        enabled: true,
                        kind: 'emails',
                        tier_key: String(selectedTierKey || selectedTier.tierKey || ''),
                      },
                    }),
                    onSuccess: () => {
                      onOpenChange(false)
                      onEmailsUpdated?.()
                      window.dispatchEvent(new CustomEvent('hubee:emails_refresh'))
                    },
                  }}
                  isEditing={false}
                />
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
